import React, { useState, useEffect, useRef, useContext } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import axios from "axios"; 
import API_ENDPOINTS from "../../config/api";
import UserContext from "../../context/UserContext";

const SupplierModal = ({ show, onClose, onSave, initialData }) => {
  const { user } = useContext(UserContext);
  const [currentUserEmail, setCurrentUserEmail] = useState("");
  const BASE_URL = API_ENDPOINTS.url;

  const [formData, setFormData] = useState({
    id: "",
    name: "",
    type: "0", // Mặc định là 0 (Supplier)
    address: "",
    phone: "",
    email: "",
    personalContact: "",
    fieldOfActivity: "",
    origin: "Việt Nam",
    image: "",
    companyProfile: "",
    beneficiaryName: "",
    bankAccountNumber: "",
    bankName: "",
    transferContent: "",
    createdBy: "",
    createdDate: new Date().toLocaleDateString('vi-VN')
  });

  const [files, setFiles] = useState({
    imageFile: null,
    profileFile: null
  });

  const [previews, setPreviews] = useState({ image: null });

  const imageRef = useRef();
  const profileRef = useRef();

  useEffect(() => {
    const fetchUserEmail = async () => {
      if (show && user?.id) {
        try {
          const token = localStorage.getItem("token");
          const response = await axios.get(`${BASE_URL}/api/User/${user.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data?.email) setCurrentUserEmail(response.data.email);
        } catch (error) {
          setCurrentUserEmail(user?.email || "system@pex.com.vn");
        }
      }
    };
    fetchUserEmail();
  }, [show, user, BASE_URL]);

  useEffect(() => {
    if (show) {
      if (initialData) {
        setFormData({ 
          ...initialData,
          // Đảm bảo type được chuyển về string để hiển thị trong <option>
          type: initialData.type !== undefined ? initialData.type.toString() : "0",
          createdBy: initialData.createdBy || "", 
          createdDate: initialData.createdDate || new Date().toLocaleDateString('vi-VN'),
          updatedBy: currentUserEmail 
        });
        setPreviews({ image: initialData.image ? `${BASE_URL}/${initialData.image}` : null });
      } else {
        setFormData({
          id: "Auto-ID",
          name: "",
          type: "0",
          address: "",
          phone: "",
          email: "",
          personalContact: "",
          fieldOfActivity: "",
          origin: "Việt Nam",
          image: "",
          companyProfile: "",
          beneficiaryName: "",
          bankAccountNumber: "",
          bankName: "",
          transferContent: "",
          createdBy: currentUserEmail, 
          createdDate: new Date().toLocaleDateString('vi-VN')
        });
        setPreviews({ image: null });
      }
      setFiles({ imageFile: null, profileFile: null });
    }
  }, [initialData, show, currentUserEmail, BASE_URL]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, key) => {
    const file = e.target.files[0];
    if (file) {
      setFiles(prev => ({ ...prev, [key]: file }));
      if (key === "imageFile") setPreviews({ image: URL.createObjectURL(file) });
    }
  };

  const openFile = (path) => {
    if (!path) return;
    const url = path.startsWith('blob:') ? path : `${BASE_URL}/${path}`;
    window.open(url, '_blank');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = new FormData();
    const cleanData = { ...formData };

    if (cleanData.id === "Auto-ID" || cleanData.id === "") cleanData.id = "0";
    if (initialData) cleanData.updatedBy = currentUserEmail;
    else cleanData.createdBy = currentUserEmail;

    delete cleanData.createdDate;

    Object.keys(cleanData).forEach(key => {
      const value = cleanData[key] ?? "";
      data.append(key, value);
    });

    if (files.imageFile) data.append("imageFile", files.imageFile);
    if (files.profileFile) data.append("profileFile", files.profileFile);
    
    onSave(data);
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton className="bg-primary text-white py-2">
        <Modal.Title className="small fw-bold text-uppercase">
          <i className="fas fa-industry me-2"></i>
          {initialData && formData.id !== "Auto-ID" ? `Edit Record: ${formData.id}` : "Add New Record"}
        </Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto' }} className="p-4">
          <Row>
            <input type="file" hidden ref={imageRef} accept="image/*" onChange={(e) => handleFileChange(e, "imageFile")} />
            <input type="file" hidden ref={profileRef} accept=".pdf" onChange={(e) => handleFileChange(e, "profileFile")} />

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold text-muted text-uppercase">ID</Form.Label>
                <Form.Control type="text" value={formData.id} readOnly className="bg-light" />
              </Form.Group>
            </Col>

            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold text-uppercase">Origin *</Form.Label>
                <Form.Select name="origin" value={formData.origin} onChange={handleChange}>
                  <option value="Việt Nam">Việt Nam</option>
                  <option value="Úc">Úc (Australia)</option>
                  <option value="Trung Quốc">Trung Quốc</option>
                  <option value="Khác">Khác</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold text-uppercase">Company Name *</Form.Label>
                <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} required />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold text-uppercase">Type *</Form.Label>
                {/* Sử dụng value dạng string (0, 1, 2) để khớp với Enum int */}
                <Form.Select name="type" value={formData.type} onChange={handleChange} required>
                  <option value="0">Supplier</option>
                  <option value="1">Customer</option>
                  <option value="2">Internal</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <div className="border-top mt-2 mb-3 pt-3">
              <h6 className="text-primary small fw-bold text-uppercase mb-3"><i className="fas fa-university me-2"></i>Banking Information</h6>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold text-uppercase">Beneficiary Name</Form.Label>
                    <Form.Control type="text" name="beneficiaryName" value={formData.beneficiaryName} onChange={handleChange} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold text-uppercase">Bank Name</Form.Label>
                    <Form.Control type="text" name="bankName" value={formData.bankName} onChange={handleChange} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold text-uppercase">Bank Account Number</Form.Label>
                    <Form.Control type="text" name="bankAccountNumber" value={formData.bankAccountNumber} onChange={handleChange} />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold text-uppercase">Transfer Content</Form.Label>
                    <Form.Control as="textarea" rows={2} name="transferContent" value={formData.transferContent} onChange={handleChange} />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold text-uppercase">Address</Form.Label>
                <Form.Control type="text" name="address" value={formData.address} onChange={handleChange} />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold text-uppercase">Phone</Form.Label>
                <Form.Control type="text" name="phone" value={formData.phone} onChange={handleChange} />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold text-uppercase">Email</Form.Label>
                <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold text-uppercase">Personal Contact</Form.Label>
                <Form.Control type="text" name="personalContact" value={formData.personalContact} onChange={handleChange} />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold text-uppercase">Field of Activity</Form.Label>
                <Form.Control type="text" name="fieldOfActivity" value={formData.fieldOfActivity} onChange={handleChange} />
              </Form.Group>
            </Col>

            <Col md={12}>
                <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold text-uppercase">Company Profile (PDF)</Form.Label>
                    <div className="d-flex gap-2">
                        <div onClick={() => profileRef.current.click()} className="flex-grow-1 border rounded py-2 px-3 bg-white shadow-sm d-flex justify-content-between align-items-center" style={{ cursor: "pointer" }}>
                            <span className="small text-muted text-truncate" style={{ maxWidth: '250px' }}>
                                {files.profileFile ? files.profileFile.name : (formData.companyProfile ? "Change profile PDF" : "Click to upload PDF")}
                            </span>
                            <i className="fas fa-upload text-primary"></i>
                        </div>
                        {(formData.companyProfile || files.profileFile) && (
                            <Button 
                                variant="outline-danger" 
                                onClick={() => openFile(files.profileFile ? URL.createObjectURL(files.profileFile) : formData.companyProfile)}
                                title="View Current Profile"
                            >
                                <i className="fas fa-eye"></i>
                            </Button>
                        )}
                    </div>
                </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Label className="small fw-bold text-uppercase">Logo / Image</Form.Label>
              <div className="position-relative">
                <div 
                  className="border rounded p-2 text-center bg-light mb-2" 
                  style={{ minHeight: "150px", cursor: "pointer" }} 
                  onClick={() => imageRef.current.click()}
                >
                    {previews.image ? (
                        <img src={previews.image} alt="Logo" style={{ maxHeight: '140px', maxWidth: '100%' }} />
                    ) : (
                        <div className="py-4"><i className="fas fa-camera fs-1 text-secondary"></i><br/><span className="small text-muted">Upload Logo</span></div>
                    )}
                </div>
                {previews.image && (
                    <Button 
                        variant="dark" 
                        size="sm" 
                        className="position-absolute bottom-0 end-0 m-3 opacity-75"
                        onClick={(e) => {
                            e.stopPropagation();
                            openFile(files.imageFile ? URL.createObjectURL(files.imageFile) : formData.image);
                        }}
                    >
                        <i className="fas fa-expand me-1"></i> View Large
                    </Button>
                )}
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className="bg-light">
          <Button variant="secondary" size="sm" onClick={onClose}>CANCEL</Button>
          <Button variant="primary" size="sm" type="submit">SAVE RECORD</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SupplierModal;